import { NgModule, APP_INITIALIZER  } from '@angular/core';
import { BrowserModule, Title } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { MaterialModule } from './modules/material/material.module';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MAT_COLOR_FORMATS, NgxMatColorPickerModule, NGX_MAT_COLOR_FORMATS } from '@angular-material-components/color-picker';

// Security
import { SecurityService } from './services/security.service';
import { RequestInterceptorService } from './services/request-interceptor.service';

//Components
import { ErrorComponent } from './components/error/error.component';
import { HeaderComponent } from './components/header/header.component';
import { FooterComponent } from './components/footer/footer.component';
import { AccueilComponent } from './components/accueil/accueil.component';
import { PublicComponent } from './components/public/public.component';
import { FoldersComponent } from './components/modules/folders/folders.component';
import { MessagesComponent } from './components/pages/messages/messages.component';
import { CreateComponent } from './components/pages/create/create.component';
import { OpenComponent } from './components/pages/open/open.component';
import { OtpComponent } from './components/pages/otp/otp.component';
import { OpenMessageUrlComponent } from './components/pages/open-message-url/open-message-url.component';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { DirectoryComponent } from './components/pages/directory/directory.component';
import { PatientInfoModalComponent } from './components/modals/patient-info-modal/patient-info-modal.component';
import { FlexLayoutModule } from '@angular/flex-layout';
import { PatientInfoEditComponent } from './components/modals/patient-info-edit/patient-info-edit.component';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { RevokeConsentComponent } from './components/pages/revoke-consent/revoke-consent.component';
import { DirectoryFormComponent } from './components/modules/directory-form/directory-form.component';
import { EditorModule, TINYMCE_SCRIPT_SRC } from "@tinymce/tinymce-angular";
import { OpenMessageComponent } from './components/modules/open-message/open-message.component';
import { SettingsComponent } from './components/settings/settings.component';
import { SettingsFoldersComponent } from './components/settings-folders/settings-folders.component';
import { SettingsUserComponent } from './components/settings-user/settings-user.component';
import { FolderEditComponent } from './components/modals/folder-edit/folder-edit.component';
import { PatientValidatorDirective } from './directives/patient-validator.directive';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import { AddPatientDirectoryComponent } from './components/modules/add-patient-directory/add-patient-directory.component';
import { SpinnerLoaderComponent } from './components/spinner-loader/spinner-loader.component';
import { UserInfoModalComponent } from './components/modals/user-info-modal/user-info-modal.component';
import { ResponseModalComponent } from './components/modals/response-modal/response-modal.component';
import { ShareDirectoryComponent } from './components/modules/share-directory/share-directory.component';
import { ColorPickerComponent } from './color-picker/color-picker.component';
import { SuperAdminComponent } from './components/pages/super-admin/super-admin.component';
import { PsAdminComponent } from './components/modules/superAdmin/ps-admin/ps-admin.component';
import { ClientAdminComponent } from './components/modules/superAdmin/client-admin/client-admin.component';
import { AccueilAdminComponent } from './components/modules/superAdmin/accueil-admin/accueil-admin.component';
import { PsFormComponent } from './components/modules/superAdmin/forms/ps-form/ps-form.component';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { PsInfoComponent } from './components/modules/superAdmin/ps-admin/modals/ps-info/ps-info.component';
import { ClientInfoComponent } from './components/modules/superAdmin/client-admin/modals/client-info/client-info.component';
import { ClientFormComponent } from './components/modules/superAdmin/forms/client-form/client-form/client-form.component';
import { MyMatPaginatorIntl } from 'src/shared/MyMatPaginatorIntl';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { CguComponent } from './components/pages/cgu/cgu.component';

export function kcFactory(kcInstance: SecurityService) {
    return ()=> kcInstance.init();
}

// AoT requires an exported function for factories
export function HttpLoaderFactory(httpClient: HttpClient) {
    return new TranslateHttpLoader(httpClient);
}

@NgModule({
    declarations: [
        AppComponent,
        ErrorComponent,
        HeaderComponent,
        FooterComponent,
        AccueilComponent,
        PublicComponent,
        FoldersComponent,
        MessagesComponent,
        CreateComponent,
        OpenComponent,
        OtpComponent,
        OpenMessageUrlComponent,
        DirectoryComponent,
        PatientInfoModalComponent,
        PatientInfoEditComponent,
        RevokeConsentComponent,
        DirectoryFormComponent,
        OpenMessageComponent,
        SettingsComponent,
        SettingsFoldersComponent,
        SettingsUserComponent,
        FolderEditComponent,
        PatientValidatorDirective,
        AddPatientDirectoryComponent,
        SpinnerLoaderComponent,
        UserInfoModalComponent,
        ResponseModalComponent,
        ShareDirectoryComponent,
        ColorPickerComponent,
        PsAdminComponent,
        ClientAdminComponent,
        AccueilAdminComponent,
        SuperAdminComponent,
        PsFormComponent,
        PsInfoComponent,
        ClientInfoComponent,
        ClientFormComponent,
        CguComponent
    ],
    imports: [
        BrowserModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        MaterialModule,
        HttpClientModule,
        ReactiveFormsModule,
        FormsModule,
        NgxMatColorPickerModule,
        NgbModule,
        FlexLayoutModule,
        EditorModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient]
            },
            defaultLanguage: 'fr'
        }),
        NgxMatSelectSearchModule
    ],
    providers: [
        { provide: APP_INITIALIZER, deps: [ SecurityService ], useFactory: kcFactory, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: RequestInterceptorService, multi: true },
        { provide: MAT_COLOR_FORMATS, useValue: NGX_MAT_COLOR_FORMATS },
        { provide: MAT_DATE_LOCALE, useValue: 'fr-FR'},
        { provide: TINYMCE_SCRIPT_SRC, useValue: 'tinymce/tinymce.min.js'},
        { provide: MatPaginatorIntl, useClass: MyMatPaginatorIntl },
        Title
    ],
    bootstrap: [AppComponent]
})

export class AppModule { }